import { ReactNode, useEffect, useState } from 'react';
import { SAVE_BUTTON_STATES } from '@urbanx/agx.ui-components2';
import { AgxToastState } from 'types/commonTypes';
import {
  SolicitorDetails,
  VendorModel,
  useInvalidateUseStaffGetPropertyInformationProgressForCampaign,
  useInvalidateUseStaffGetVendorDetailsForCampaign,
  useStaffGetVendorDetailsForCampaign,
  useStaffUpdateVendorDetailsForCampaign,
} from '@urbanx/agx.campaigns.client';
import { isDirtyEqual } from 'utils/compares';
import { getCrmFieldChangeChecks } from 'pages/campaigns/components/manageCampaign/CrmDataUpdateHelper';
import useCrmDisconnectedModal from 'pages/campaigns/components/manageCampaign/useCrmDisconnectedModal';

interface UseVendorDetailsSectionStateArgs {
  campaignId?: string;
  updateToastState: (toastState: AgxToastState) => void;
}

const ignoredVendorFields = ['type', 'stakeholderType'];

interface VendorDetailsSectionState {
  vendorDetails: VendorModel[];
  setVendorDetails: (value: VendorModel[]) => void;
  solicitorDetails: SolicitorDetails | undefined;
  setSolicitorDetails: (value: SolicitorDetails | undefined) => void;
  exportedIdentityVerificationSourceIds: string[];
  hasUnsavedChanges: boolean;
  saveState: (typeof SAVE_BUTTON_STATES)[keyof typeof SAVE_BUTTON_STATES];
  isSaving: boolean;
  isPending: boolean;
  isError: boolean;
  handleSaveChanges: () => Promise<void>;
  crmDisconnectedModal: ReactNode;
}

const useVendorDetailsSectionState = ({
  campaignId,
  updateToastState,
}: UseVendorDetailsSectionStateArgs): VendorDetailsSectionState => {
  const [vendorDetails, setVendorDetails] = useState<VendorModel[]>([]);
  const [solicitorDetails, setSolicitorDetails] = useState<SolicitorDetails | undefined>();
  const [isSaving, setIsSaving] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const [hasError, setHasError] = useState(false);
  const { modal: crmDisconnectedModal, openWithChanges } = useCrmDisconnectedModal();

  const {
    data: vendorDetailsResponse,
    isPending,
    isError,
  } = useStaffGetVendorDetailsForCampaign(campaignId ?? '', !!campaignId);

  const invalidateStaffGetVendorDetails = useInvalidateUseStaffGetVendorDetailsForCampaign();
  const invalidateStaffGetPropertyInformationProgress =
    useInvalidateUseStaffGetPropertyInformationProgressForCampaign();

  const { staffUpdateVendorDetailsForCampaign } = useStaffUpdateVendorDetailsForCampaign({
    onSuccess: () => undefined,
    onError: () => undefined,
  });
  const exportedIdentityVerificationSourceIds =
    vendorDetailsResponse?.exportedIdentityVerificationSourceIds ?? [];

  useEffect(() => {
    setVendorDetails(vendorDetailsResponse?.vendors ?? []);
  }, [vendorDetailsResponse?.vendors]);

  useEffect(() => {
    setSolicitorDetails(vendorDetailsResponse?.vendorSolicitor);
  }, [vendorDetailsResponse?.vendorSolicitor]);

  const hasUnsavedChanges = !(
    isDirtyEqual(vendorDetails, vendorDetailsResponse?.vendors ?? [], ignoredVendorFields) &&
    isDirtyEqual(solicitorDetails, vendorDetailsResponse?.vendorSolicitor)
  );

  useEffect(() => {
    if (hasUnsavedChanges) {
      setHasSaved(false);
      setHasError(false);
    }
  }, [hasUnsavedChanges]);

  const saveState = (() => {
    if (isSaving) return SAVE_BUTTON_STATES.SAVING;
    if (hasError) return SAVE_BUTTON_STATES.ERROR;
    if (hasSaved) return SAVE_BUTTON_STATES.SAVED;
    if (hasUnsavedChanges) return SAVE_BUTTON_STATES.UNSAVED;
    return SAVE_BUTTON_STATES.UNSAVED;
  })();

  const handleSaveChanges = async () => {
    if (!campaignId || !hasUnsavedChanges || isSaving) return;

    setIsSaving(true);
    setHasError(false);
    try {
      const changes = [
        ...getCrmFieldChangeChecks('Vendors', vendorDetails, {
          Vendors: vendorDetailsResponse?.vendors ?? [],
        }),
        ...getCrmFieldChangeChecks('VendorSolicitor', solicitorDetails, {
          VendorSolicitor: vendorDetailsResponse?.vendorSolicitor,
        }),
      ]
        .filter(({ changed }) => changed)
        .map(({ field, value }) => ({ field, value }));

      openWithChanges(changes);

      await staffUpdateVendorDetailsForCampaign({
        campaignId,
        body: {
          vendors: vendorDetails,
          vendorSolicitor: solicitorDetails,
        },
      });
      await Promise.all([
        invalidateStaffGetVendorDetails(campaignId),
        invalidateStaffGetPropertyInformationProgress(campaignId),
      ]);
      setHasSaved(true);
      updateToastState({
        color: 'success',
        message: 'Vendor details saved successfully',
        open: true,
      });
    } catch {
      setHasError(true);
      updateToastState({
        color: 'error',
        message: 'Error saving vendor details',
        open: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return {
    vendorDetails,
    setVendorDetails,
    solicitorDetails,
    setSolicitorDetails,
    exportedIdentityVerificationSourceIds,
    hasUnsavedChanges,
    saveState,
    isSaving,
    isPending,
    isError,
    handleSaveChanges,
    crmDisconnectedModal,
  };
};

export default useVendorDetailsSectionState;
